import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../layouts/default";
import { Button, Table, Spinner, Modal, Form, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  downloadPreLeadMarketing,
  fetchPreLeadDownloadHistory,
} from "../../services/preLeads/preLeadMarketing";
import { PaginationList } from "../Pagination/Pagination";
const MAX_DATE_GAP_DAYS = 15;

const PreLeads = () => {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  // Table filter (separate from download filter)
  const [tableFromDate, setTableFromDate] = useState("");
  const [tableToDate, setTableToDate] = useState("");
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [dlFromDate, setDlFromDate] = useState("");
  const [dlToDate, setDlToDate] = useState("");
  const [dlToMin, setDlToMin] = useState("");
  const [dlToMax, setDlToMax] = useState("");
  const [dateError, setDateError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const loadHistory = useCallback(
    async (
      pageNum = 1,
      filterFromDate = appliedFromDate,
      filterToDate = appliedToDate,
    ) => {
      setLoadingHistory(true);
      try {
        const res = await fetchPreLeadDownloadHistory(pageNum, 10, {
          from_date: filterFromDate,
          to_date: filterToDate,
        });
        if (res?.success && Array.isArray(res.data)) {
          setHistory(res.data);
          if (res.pagination) setPagination(res.pagination);
        }
      } catch (e) {
        console.error("Fetch history error:", e);
      } finally {
        setLoadingHistory(false);
      }
    },
    [appliedFromDate, appliedToDate],
  );

  useEffect(() => {
    loadHistory(page);
  }, [loadHistory, page]);

  const handleApplyFilter = async () => {
    setAppliedFromDate(tableFromDate);
    setAppliedToDate(tableToDate);
    setPage(1);
    await loadHistory(1, tableFromDate, tableToDate);
  };

  const handleResetFilter = async () => {
    setTableFromDate("");
    setTableToDate("");
    setAppliedFromDate("");
    setAppliedToDate("");
    setPage(1);
    await loadHistory(1, "", "");
  };

  const handleOpenModal = () => {
    setDlFromDate("");
    setDlToDate("");
    setDlToMin("");
    setDlToMax("");
    setDateError("");
    setShowModal(true);
  };

  const handleModalClose = () => {
    if (downloading) return; 
    setShowModal(false);
  };

  const handleDlFromDateChange = (e) => {
    const from = e.target.value;
    setDlFromDate(from);
    setDlToDate("");
    setDateError("");

    if (from) {
      const fromObj = new Date(from);
      const minTo = new Date(fromObj);
      minTo.setDate(minTo.getDate() + 1);
      const maxTo = new Date(fromObj);
      maxTo.setDate(maxTo.getDate() + MAX_DATE_GAP_DAYS);

      setDlToMin(minTo.toISOString().split("T")[0]);
      setDlToMax(maxTo.toISOString().split("T")[0]);
    } else {
      setDlToMin("");
      setDlToMax("");
    }
  };

  const handleDlToDateChange = (e) => {
    setDlToDate(e.target.value);
    setDateError("");
  };

  const handleStartDownload = async () => {
    if (!dlFromDate || !dlToDate) {
      setDateError("Please select both From Date and To Date.");
      return;
    }

    const from = new Date(dlFromDate);
    const to = new Date(dlToDate);
    const diffDays = (to - from) / (1000 * 60 * 60 * 24);

    if (diffDays <= 0) {
      setDateError("To Date must be after From Date.");
      return;
    }

    if (diffDays > MAX_DATE_GAP_DAYS) {
      setDateError(`Date range cannot exceed ${MAX_DATE_GAP_DAYS} days.`);
      return;
    }

    setDateError("");
    setDownloading(true);
    try {
      await downloadPreLeadMarketing({
        from_date: dlFromDate,
        to_date: dlToDate,
      });

      toast.success("File downloaded successfully", { autoClose: 3000 });
      setShowModal(false);
      loadHistory(page);
    } catch (e) {
      toast.error("Failed to download file", { autoClose: 3000 });
      console.error("Download error:", e);
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  };

  return (
    <Layout>
      <div className="mb-4">
        <p className="fw-semibold mb-3" style={{ color: "#6c757d" }}>
          Download Marketing lead file
        </p>

        <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
          <div>
            <label className="form-label fw-semibold">From Date</label>
            <input
              type="date"
              className="form-control"
              value={tableFromDate}
              onChange={(e) => setTableFromDate(e.target.value)}
            />
          </div>

          <div>
            <label className="form-label fw-semibold">To Date</label>
            <input
              type="date"
              className="form-control"
              value={tableToDate}
              onChange={(e) => setTableToDate(e.target.value)}
            />
          </div>

          <Button variant="success" onClick={handleApplyFilter}>
            Apply Filter
          </Button>

          <Button variant="secondary" onClick={handleResetFilter}>
            Reset
          </Button>

          {/* Download button opens modal */}
          <Button variant="primary" onClick={handleOpenModal}>
            Download
          </Button>
        </div>
      </div>

      <div className="mt-4">
        {loadingHistory ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Table responsive bordered hover>
            <thead style={{ backgroundColor: "#e8eaf0" }}>
              <tr>
                <th>SR NO.</th>
                <th>Date</th>
                <th>Download Count</th>
                <th>Download By</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-4">
                    No download history found.
                  </td>
                </tr>
              ) : (
                history.map((item, index) => (
                  <tr key={item.id}>
                    <td>
                      {(page - 1) * (pagination?.limit || 10) + index + 1}.
                    </td>
                    <td>{formatDate(item.created_at)}</td>
                    <td>{item.lead_count}</td>
                    <td>{item.downloaded_by_name}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}

        {pagination && pagination.total_pages > 1 && (
          <PaginationList
            paginationData={{
              page,
              total: pagination.total,
              recordsPerPage: pagination.limit,
              template: "pre-leads",
            }}
            setPage={(p) => setPage(p)}
            setLoading={setLoadingHistory}
          />
        )}
      </div>

      <Modal show={showModal} size="md" onHide={handleModalClose} centered>
        <Modal.Header closeButton={!downloading}>
          <Modal.Title>Download Marketing Leads</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="text-muted mb-4" style={{ fontSize: "0.9rem" }}>
            Select a date range (max <strong>{MAX_DATE_GAP_DAYS} days</strong>)
            to download the lead file. Multiple files will be zipped
            automatically.
          </p>

          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="dlFromDate">
                <Form.Label className="fw-semibold">From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dlFromDate}
                  onChange={handleDlFromDateChange}
                  disabled={downloading}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="dlToDate">
                <Form.Label className="fw-semibold">To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dlToDate}
                  onChange={handleDlToDateChange}
                  disabled={!dlFromDate || downloading}
                  min={dlToMin}
                  max={dlToMax}
                />
                {dlFromDate && (
                  <Form.Text className="text-muted">
                    Max range: {MAX_DATE_GAP_DAYS} days from From Date
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          {dateError && (
            <p
              className="text-danger mt-3 mb-0"
              style={{ fontSize: "0.875rem" }}
            >
              {dateError}
            </p>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={downloading}
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleStartDownload}
            disabled={downloading || !dlFromDate || !dlToDate}
          >
            {downloading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Downloading...
              </>
            ) : (
              "Start Download"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default PreLeads;
